// ---------------------------------------------------------------------------
// A cover image taken from the video file itself.
// ---------------------------------------------------------------------------
// Instagram and Facebook hand out no cover image for an embedded post — Meta
// removed thumbnail_url from oEmbed — so those cards fall back to a designed
// placeholder while TikTok and YouTube show a real frame.
//
// The embed can't help: it is a cross-origin iframe, and the same-origin
// policy stops any page reading pixels out of one. A video file the person
// chooses is a different matter — it belongs to the page, so the browser will
// decode it and hand over a frame.
//
// So whoever adds the video can point at the clip on their phone and we take
// the first proper frame from it. The clip itself is never uploaded; only the
// frame leaves the device.
// ---------------------------------------------------------------------------

import { fitWithin } from './imageResize';

// Far enough in to miss a black or title frame, close enough to still be the
// opening shot. Clamped for clips shorter than that.
const TARGET_SECONDS = 1;
const QUALITY = 0.82;
// Covers render at 150px wide in the shelf and about 420px in the player, so
// 720 on the long edge is retina there with room to spare.
const MAX_DIMENSION = 720;
// A phone can take a few seconds to decode the first frame of a 4K clip; past
// this something is wrong (an unsupported codec usually hangs rather than
// erroring).
const TIMEOUT_MS = 20000;
// How long to wait for a seek before settling for the frame already decoded.
const SEEK_FALLBACK_MS = 3000;
// And how long to wait for that frame to be painted before copying anyway.
const PAINT_FALLBACK_MS = 400;

export class FrameError extends Error {}

function cleanUp(video, url) {
  video.removeAttribute('src');
  video.load();
  URL.revokeObjectURL(url);
}

// Resolves to a JPEG File of one frame, ready to upload. Rejects with a
// FrameError whose message can be shown as it is.
export function grabVideoFrame(file, { seconds = TARGET_SECONDS } = {}) {
  return new Promise((resolve, reject) => {
    if (!file || !/^video\//.test(file.type || '')) {
      reject(new FrameError('Choose a video file and we’ll take the cover from it.'));
      return;
    }

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    // Muted + playsInline so mobile Safari will decode without a gesture.
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';

    let settled = false;
    let seekFallback;
    let retriedFromStart = false;
    const fail = (message) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearTimeout(seekFallback);
      cleanUp(video, url);
      reject(new FrameError(message));
    };
    const timer = setTimeout(
      () => fail('That video took too long to read. Try a shorter clip, or add a cover another way.'),
      TIMEOUT_MS
    );

    video.onerror = () => fail("This browser can't read that video. An MP4 usually works.");

    video.onloadedmetadata = () => {
      const { videoWidth: w, videoHeight: h, duration } = video;
      if (!w || !h) {
        fail("That file doesn't seem to have any video in it.");
        return;
      }
      // Seeking past the end leaves some browsers waiting forever, so a known
      // duration is clamped. An unknown one (a clip saved straight off a
      // camera app often has no duration in its header) still gets the seek —
      // the browser clamps it — but with a short fallback, because a file
      // that cannot seek would otherwise sit there until the timeout.
      const known = Number.isFinite(duration) && duration > 0;
      const target = known ? Math.min(seconds, Math.max(0, duration - 0.05)) : seconds;
      if (target <= 0) {
        capture();
        return;
      }
      seekFallback = setTimeout(capture, SEEK_FALLBACK_MS);
      video.currentTime = target;
    };

    // A finished seek does not mean there is a frame to copy yet: the browser
    // reports `seeked` when it has moved, and paints shortly after. Drawing in
    // that gap gives a blank canvas, which is how this first went wrong.
    // requestVideoFrameCallback fires once a frame is actually on screen.
    video.onseeked = () => {
      clearTimeout(seekFallback);
      if (settled) return;
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(() => capture());
        // …and a backstop, since a paused element in some browsers never
        // presents another frame.
        seekFallback = setTimeout(capture, PAINT_FALLBACK_MS);
      } else {
        seekFallback = setTimeout(capture, PAINT_FALLBACK_MS);
      }
    };

    // Nothing but a dark rectangle means we caught the decoder mid-seek, or
    // the clip opens on black. Either way the restaurant's own photo beats a
    // black square, so say so rather than storing it.
    function looksBlank(ctx, width, height) {
      const { data } = ctx.getImageData(0, 0, width, height);
      const step = Math.max(4, Math.floor(data.length / 4 / 2000) * 4);
      let lit = 0;
      let seen = 0;
      for (let i = 0; i < data.length; i += step) {
        seen += 1;
        if (data[i] > 24 || data[i + 1] > 24 || data[i + 2] > 24) lit += 1;
      }
      return seen > 0 && lit / seen < 0.02;
    }

    function capture() {
      if (settled) return;
      clearTimeout(seekFallback);
      try {
        const { width, height } = fitWithin(video.videoWidth, video.videoHeight, MAX_DIMENSION);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, width, height);

        if (looksBlank(ctx, width, height)) {
          // One retry from the very start, where there is always a decoded
          // frame; if that is black too, the clip opens on black.
          if (!retriedFromStart && video.currentTime > 0) {
            retriedFromStart = true;
            video.currentTime = 0;
            seekFallback = setTimeout(capture, PAINT_FALLBACK_MS);
            return;
          }
          fail("We couldn't get a usable frame out of that clip — its opening looks blank.");
          return;
        }

        canvas.toBlob(
          (blob) => {
            if (settled) return;
            if (!blob) {
              fail("We couldn't turn that frame into an image. Try another clip.");
              return;
            }
            settled = true;
            clearTimeout(timer);
            cleanUp(video, url);
            resolve(new File([blob], 'cover.jpg', { type: 'image/jpeg' }));
          },
          'image/jpeg',
          QUALITY
        );
      } catch (err) {
        // A DRM-protected or otherwise tainted source throws here.
        fail("That video can't be read for a cover image.");
      }
    }

    video.src = url;
  });
}
